import { useReducer, useCallback, useRef, useMemo } from 'react';
import {
  NavigationService,
  Position,
  NavigationProgress,
} from '@client/services/NavigationService';

export interface NavigationMachineState {
  type: 'loading' | 'idle' | 'saving' | 'error';
  currentPosition: Position | null;
  visitedSubsections: Set<string>;
  formData: PolicyFormSubmissionData;
  progress: NavigationProgress | null;
  error?: string;
  pendingNavigation?: Position;
}

export type NavigationMachineAction =
  | {
      type: 'INITIALIZE';
      formData: PolicyFormSubmissionData;
      visitedSubsections: string[];
      initialPosition?: Position;
    }
  | { type: 'NAVIGATE'; position: Position }
  | { type: 'UPDATE_FORM_DATA'; data: PolicyFormSubmissionData }
  | { type: 'START_SAVE'; pendingNavigation?: Position }
  | { type: 'SAVE_SUCCESS'; newPosition?: Position }
  | { type: 'SAVE_ERROR'; error: string }
  | { type: 'MARK_VISITED'; subsectionId: string }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'CLEAR_ERROR' };

const initialState: NavigationMachineState = {
  type: 'loading',
  currentPosition: null,
  visitedSubsections: new Set(),
  formData: {},
  progress: null,
};

function navigationReducer(
  state: NavigationMachineState,
  action: NavigationMachineAction,
  navigationService: NavigationService,
  parameters: Record<string, any> = {},
  wizardUnrestrictedNavEnabled: boolean = false,
): NavigationMachineState {
  switch (action.type) {
    case 'INITIALIZE': {
      const visitedSubsections = new Set(action.visitedSubsections);
      const currentPosition = action.initialPosition || navigationService.getFirstPosition();
      const progress = navigationService.calculateProgress(
        action.formData,
        visitedSubsections,
        currentPosition.subsectionId,
        parameters,
      );

      visitedSubsections.add(currentPosition.subsectionId);

      return {
        ...state,
        type: 'idle',
        currentPosition,
        visitedSubsections,
        formData: action.formData,
        progress,
      };
    }

    case 'NAVIGATE': {
      if (state.type === 'saving') {
        return state;
      }

      // Wizard unrestricted navigation bypass: skip validation check
      if (!wizardUnrestrictedNavEnabled) {
        const validation = navigationService.canNavigateToPosition(
          action.position,
          state.formData,
          state.visitedSubsections,
          state.currentPosition?.subsectionId,
        );

        if (!validation.canNavigate) {
          return {
            ...state,
            type: 'error',
            error: validation.reason || 'Navigation not allowed',
          };
        }
      }

      const updatedVisited = new Set(state.visitedSubsections);
      updatedVisited.add(action.position.subsectionId);

      const progress = navigationService.calculateProgress(
        state.formData,
        updatedVisited,
        action.position.subsectionId,
        parameters,
      );

      return {
        ...state,
        type: 'idle',
        currentPosition: action.position,
        visitedSubsections: updatedVisited,
        progress,
        error: undefined,
      };
    }

    case 'UPDATE_FORM_DATA': {
      if (!state.currentPosition) return state;

      const progress = navigationService.calculateProgress(
        action.data,
        state.visitedSubsections,
        state.currentPosition.subsectionId,
        parameters,
      );

      return {
        ...state,
        formData: action.data,
        progress,
      };
    }

    case 'START_SAVE': {
      return {
        ...state,
        type: 'saving',
        pendingNavigation: action.pendingNavigation,
      };
    }

    case 'SAVE_SUCCESS': {
      let newPosition = state.currentPosition;

      if (action.newPosition) {
        newPosition = action.newPosition;
      } else if (state.pendingNavigation) {
        newPosition = state.pendingNavigation;
      }

      if (!newPosition) return { ...state, type: 'idle', pendingNavigation: undefined };

      const updatedVisited = new Set(state.visitedSubsections);
      updatedVisited.add(newPosition.subsectionId);

      const progress = navigationService.calculateProgress(
        state.formData,
        updatedVisited,
        newPosition.subsectionId,
        parameters,
      );

      return {
        ...state,
        type: 'idle',
        currentPosition: newPosition,
        visitedSubsections: updatedVisited,
        progress,
        pendingNavigation: undefined,
      };
    }

    case 'SAVE_ERROR': {
      return {
        ...state,
        type: 'error',
        error: action.error,
        pendingNavigation: undefined,
      };
    }

    case 'MARK_VISITED': {
      if (state.visitedSubsections.has(action.subsectionId)) return state;

      const updatedVisited = new Set(state.visitedSubsections);
      updatedVisited.add(action.subsectionId);

      const progress = state.currentPosition
        ? navigationService.calculateProgress(
            state.formData,
            updatedVisited,
            state.currentPosition.subsectionId,
            parameters,
          )
        : state.progress;

      return {
        ...state,
        visitedSubsections: updatedVisited,
        progress,
      };
    }

    case 'SET_ERROR': {
      return {
        ...state,
        type: 'error',
        error: action.error,
      };
    }

    case 'CLEAR_ERROR': {
      return {
        ...state,
        type: 'idle',
        error: undefined,
      };
    }

    default:
      return state;
  }
}

export function useNavigationStateMachine(
  navigationService: NavigationService,
  parameters: Record<string, any> = {},
  wizardUnrestrictedNavEnabled: boolean = false,
) {
  const serviceRef = useRef(navigationService);
  serviceRef.current = navigationService;

  const [state, dispatch] = useReducer(
    (state: NavigationMachineState, action: NavigationMachineAction) =>
      navigationReducer(
        state,
        action,
        serviceRef.current,
        parameters,
        wizardUnrestrictedNavEnabled,
      ),
    initialState,
  );

  const initialize = useCallback(
    (
      formData: PolicyFormSubmissionData,
      visitedSubsections: string[] = [],
      initialPosition?: Position,
    ) => {
      dispatch({
        type: 'INITIALIZE',
        formData,
        visitedSubsections,
        initialPosition,
      });
    },
    [],
  );

  const navigateTo = useCallback((position: Position) => {
    dispatch({ type: 'NAVIGATE', position });
  }, []);

  const navigateNext = useCallback(() => {
    if (!state.currentPosition) return;

    const nextPosition = navigationService.getNextVisiblePosition(
      state.currentPosition,
      state.formData,
      parameters,
    );

    if (nextPosition) {
      const validation = navigationService.canNavigateFromPosition(
        state.currentPosition,
        state.formData,
        parameters,
      );

      if (validation.canNavigate) {
        dispatch({ type: 'START_SAVE', pendingNavigation: nextPosition });
      } else {
        dispatch({
          type: 'SET_ERROR',
          error: validation.reason || 'Cannot navigate',
        });
      }
    }
  }, [state.currentPosition, state.formData, navigationService, parameters]);

  const navigatePrevious = useCallback(() => {
    if (!state.currentPosition) return;

    const previousPosition = navigationService.getPreviousVisiblePosition(
      state.currentPosition,
      state.formData,
      parameters,
    );
    if (previousPosition) {
      navigateTo(previousPosition);
    }
  }, [state.currentPosition, state.formData, navigateTo, navigationService]);

  const updateFormData = useCallback((data: PolicyFormSubmissionData) => {
    dispatch({ type: 'UPDATE_FORM_DATA', data });
  }, []);

  const saveSuccess = useCallback((newPosition?: Position) => {
    dispatch({ type: 'SAVE_SUCCESS', newPosition });
  }, []);

  const saveError = useCallback((error: string) => {
    dispatch({ type: 'SAVE_ERROR', error });
  }, []);

  const markVisited = useCallback((subsectionId: string) => {
    dispatch({ type: 'MARK_VISITED', subsectionId });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const canGoNext = useMemo(() => {
    if (!state.currentPosition) return false;
    return navigationService.canNavigateFromPosition(
      state.currentPosition,
      state.formData,
      parameters,
    ).canNavigate;
  }, [state.currentPosition, state.formData, navigationService, parameters]);

  const canGoPrevious = useMemo(() => {
    if (!state.currentPosition) return false;
    return (
      navigationService.getPreviousVisiblePosition(
        state.currentPosition,
        state.formData,
        parameters,
      ) !== null
    );
  }, [state.currentPosition, state.formData, navigationService, parameters]);

  const isLastStep = useMemo(() => {
    if (!state.currentPosition) return false;
    return (
      navigationService.getNextVisiblePosition(
        state.currentPosition,
        state.formData,
        parameters,
      ) === null
    );
  }, [state.currentPosition, state.formData, navigationService, parameters]);

  return {
    state,
    actions: {
      initialize,
      navigateTo,
      navigateNext,
      navigatePrevious,
      updateFormData,
      saveSuccess,
      saveError,
      markVisited,
      clearError,
    },
    computed: {
      canGoNext,
      canGoPrevious,
      isLastStep,
      isLoading: state.type === 'loading',
      isSaving: state.type === 'saving',
      hasError: state.type === 'error',
    },
  };
}
